import { useContext, useEffect } from 'react';
import {
  AppConfigContext,
  IMenuItemBadgeTextInfo,
} from '../contexts/appConfigContext';
import request, { getSessionJwt } from '../utils/request';
import useWebSocket from './useWebSocket';
import { logInfo } from '../utils/remoteLogger';
import {
  getTicketWebSocketUrl,
  releaseTicketWebSocketUrl,
} from '../utils/webSocketTicket';

interface IUpdateTabBadgeMessageContent {
  action: 'update_tab_badge';
  payload: {
    tabBadgeUpdate: {
      id: string;
      textInfo: IMenuItemBadgeTextInfo;
    };
  };
}

interface IStartVideoMeetingMessageContent {
  action: 'start_video_meeting';
  payload: {
    callerId: string;
    callerSubtitle: string;
    callerTitle: string;
    meetingId: string;
  };
}

interface IMissedCallMessageContent {
  action: 'missed_call';
  payload: null;
}

interface INormalMessageContent {
  action: 'normal';
  priority?: string;
  body?: string;
  title?: string | null;
  url?: string;
  badge?: number;
  imageUrl?: string | null;
  payload?: {
    inbox?: boolean;
    [key: string]: unknown;
  };
  silent?: boolean;
}

type IPushNotificationMessageContent =
  | IUpdateTabBadgeMessageContent
  | IStartVideoMeetingMessageContent
  | IMissedCallMessageContent
  | INormalMessageContent;

interface IRegisterTokenWebSocketMessage {
  type: 'register_token';
  content: {
    token: string;
  };
}

interface IPushNotificationWebSocketMessage {
  type: 'push_notification';
  content: IPushNotificationMessageContent;
}

type IWebSocketMessage =
  | IRegisterTokenWebSocketMessage
  | IPushNotificationWebSocketMessage;

type IWsPushNotification<T> = T extends 'update_tab_badge'
  ? IUpdateTabBadgeMessageContent
  : T extends 'start_video_meeting'
  ? IStartVideoMeetingMessageContent
  : T extends 'missed_call'
  ? IMissedCallMessageContent
  : T extends 'normal'
  ? INormalMessageContent
  : unknown;

// Keeps track of whether or not the PNS token has been registered,
// as we might have multiple instances of the hook when the token message arrives.
let pnsTokenHandled = false;

const useWsPushNotification = <
  T extends IPushNotificationMessageContent['action']
>(
  action: T
): IWsPushNotification<T> | undefined => {
  const { isUserLoggedIn } = useContext(AppConfigContext);

  // Connect to the WebSocket service once the user is logged in.
  // The connection is shared and any additional instances of this hook will share the same connection.
  // Once all the instances have been destroyed, the socket is closed.
  // An embedded session, which runs on a JWT instead of the cookie,
  // authenticates the connection with a ticket.
  const message = useWebSocket<IWebSocketMessage>(
    isUserLoggedIn ? '/api/websocket' : undefined,
    {
      shareConnection: true,
      resolveUrl: getSessionJwt() ? getTicketWebSocketUrl : undefined,
      onDisconnect: () => {
        releaseTicketWebSocketUrl();
        logInfo(
          'useWsPushNotification',
          'WebSocket disconnected, resetting PNS token handled state'
        );
        pnsTokenHandled = false;
      },
    }
  );

  // A ticket fetched for a connection that was torn down before it opened
  // never gets released by a disconnect, and would be expired by the next login.
  useEffect(() => {
    if (!isUserLoggedIn) {
      releaseTicketWebSocketUrl();
    }
  }, [isUserLoggedIn]);

  // Register the received token with the push notification service.
  useEffect(() => {
    if (message && !pnsTokenHandled) {
      if (message.type === 'register_token') {
        pnsTokenHandled = true;
        logInfo(
          'useWsPushNotification',
          'Registering PNS token with push notification service'
        );

        request('/api/push-notification/user/token', {
          method: 'POST',
          body: {
            token: message.content.token,
          },
          silent: true,
          persistent: true,
          retryable: true,
        });
      }
    }
  }, [message]);

  // Return the push notification message only if the content matches the requested type.
  return message &&
    message.type === 'push_notification' &&
    message.content.action === action
    ? (message.content as IWsPushNotification<T>)
    : undefined;
};

export default useWsPushNotification;
